import { lazy } from 'react';

// React.lazy wrapper that survives deploys.
//
// When a new build ships while a tab is still running the previous build, the
// old code-split chunk filenames (e.g. /static/js/4435.<hash>.chunk.js) no
// longer exist on the server. Navigating to a not-yet-loaded route then throws
// "Loading chunk … failed". The cure is simply to reload: the network-first
// service worker fetches the fresh index.html, which points at the new bundle.
//
// We force exactly one reload, guarded by a sessionStorage flag so a genuinely
// unreachable chunk (offline, real 404) doesn't cause an infinite reload loop.
const RELOAD_KEY = 'zb-chunk-reloaded';

export function isChunkLoadError(err) {
  const message = err?.message || '';
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk [\w]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(message)
  );
}

export default function lazyWithReload(factory) {
  return lazy(async () => {
    try {
      const mod = await factory();
      // Success — clear the guard so a future deploy can reload again.
      sessionStorage.removeItem(RELOAD_KEY);
      return mod;
    } catch (err) {
      if (isChunkLoadError(err) && !sessionStorage.getItem(RELOAD_KEY)) {
        sessionStorage.setItem(RELOAD_KEY, '1');
        window.location.reload();
        // Keep the Suspense fallback on screen until the reload takes over.
        return new Promise(() => {});
      }
      throw err;
    }
  });
}
